import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import { FileText } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { formatDayMonth } from '@/shared/lib/format';
import type { Citation } from '../api/schemas';

/** Every source as a chip that opens the report at the cited page. */
export function CitationList({ citations }: { citations: Citation[] }) {
  if (citations.length === 0) return null;
  return (
    <Stack component="ol" aria-label="Sources" direction="row" sx={{ listStyle: 'none', m: 0, p: 0, gap: 0.75, flexWrap: 'wrap' }}>
      {citations.map((c) => (
        <li key={c.n}>
          <ButtonBase
            component={RouterLink}
            to={`/documents/${c.documentId}?page=${c.page}`}
            aria-label={`Source ${c.n}: ${c.title}, ${formatDayMonth(c.publishDate)}, page ${c.page}`}
            sx={chipSx}
          >
            <Box component="span" sx={numberSx}>{c.n}</Box>
            <FileText size={13} aria-hidden />
            <Box component="span" sx={{ fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</Box>
            <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{formatDayMonth(c.publishDate)} · p.{c.page}</Box>
          </ButtonBase>
        </li>
      ))}
    </Stack>
  );
}

const chipSx = {
  gap: 0.75,
  height: 28,
  pl: 0.5,
  pr: 1.25,
  borderRadius: 1.5,
  fontSize: '0.75rem',
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  transition: 'border-color 150ms, background-color 150ms',
  '& svg': { color: 'text.secondary', flexShrink: 0 },
  '&:hover': { borderColor: 'primary.light', bgcolor: 'surface.sidebar' },
} as const;

const numberSx = {
  display: 'grid',
  placeItems: 'center',
  minWidth: 18,
  height: 18,
  px: 0.5,
  borderRadius: 0.75,
  fontSize: '0.6875rem',
  fontWeight: 600,
  bgcolor: 'surface.accentSoft',
  color: 'primary.main',
} as const;
